import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type {
  EncuestaPregunta,
  EncuestaRecent,
  EncuestasConfig,
  EncuestasMetrics,
  Tenant,
} from "../lib/api";

const TIPO_LABEL: Record<string, string> = {
  choice: "Opción múltiple",
  text: "Texto libre",
  boolean: "Sí / No",
};

export function TenantEncuestasPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [cfg, setCfg] = useState<EncuestasConfig | null>(null);
  const [metrics, setMetrics] = useState<EncuestasMetrics | null>(null);
  const [recent, setRecent] = useState<EncuestaRecent[]>([]);
  const [draft, setDraft] = useState<EncuestaPregunta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      tenants.get(id),
      tenants.getEncuestasConfig(id),
      tenants.getEncuestasMetrics(id, 90).catch(() => null),
      tenants.getEncuestasRecent(id, 10).catch(() => ({ items: [], total: 0 })),
    ])
      .then(([t, c, m, r]) => {
        setTenant(t);
        setCfg(c);
        setDraft(c.preguntas);
        setMetrics(m);
        setRecent(r?.items ?? []);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo cargar"))
      .finally(() => setLoading(false));
  }, [id]);

  const addPregunta = (tipo: "choice" | "text" | "boolean") => {
    if (draft.length >= (cfg?.max_preguntas ?? 10)) {
      flash("err", `Máximo ${cfg?.max_preguntas ?? 10} preguntas`);
      return;
    }
    setDraft([
      ...draft,
      {
        tipo,
        label: "",
        required: false,
        ...(tipo === "choice" ? { options: ["Opción 1", "Opción 2"] } : {}),
      },
    ]);
  };

  const update = (idx: number, patch: Partial<EncuestaPregunta>) => {
    setDraft(draft.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const remove = (idx: number) => setDraft(draft.filter((_, i) => i !== idx));

  const move = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= draft.length) return;
    const copy = [...draft];
    const a = copy[idx];
    const b = copy[next];
    if (!a || !b) return;
    copy[idx] = b;
    copy[next] = a;
    setDraft(copy);
  };

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const c = await tenants.updateEncuestasConfig(id, draft);
      setCfg(c);
      setDraft(c.preguntas);
      flash("ok", "Preguntas guardadas");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant || !cfg) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · Encuestas
        </div>
        <h1 style={{ fontSize: 28, marginTop: 6 }}>Encuesta de satisfacción</h1>
        <p className="lede" style={{ maxWidth: 720 }}>
          Las 3 preguntas base (estrellas, ¿resolvió?, comentario) se envían
          a todos los ciudadanos. Aquí puedes agregar preguntas adicionales
          específicas para esta alcaldía (hasta {cfg.max_preguntas}).
        </p>

        {/* Métricas */}
        {metrics && metrics.respondidas > 0 && (
          <div className="card" style={{ marginTop: 22 }}>
            <div className="card-inner">
              <div className="eyebrow">Métricas — últimos 90 días</div>
              <div className="enc-panel-metric-grid">
                <Metric
                  label="Rating"
                  value={
                    metrics.rating_promedio !== null
                      ? `${metrics.rating_promedio.toFixed(1)} / 5`
                      : "—"
                  }
                />
                <Metric
                  label="Respuesta"
                  value={`${Math.round(metrics.tasa_respuesta * 100)}%`}
                  sub={`${metrics.respondidas}/${metrics.enviadas}`}
                />
                <Metric
                  label="Resolvió"
                  value={
                    metrics.resolvio_si_pct !== null
                      ? `${Math.round(metrics.resolvio_si_pct * 100)}%`
                      : "—"
                  }
                />
                <Metric
                  label="NPS"
                  value={
                    metrics.nps_aprox !== null
                      ? metrics.nps_aprox > 0
                        ? `+${metrics.nps_aprox}`
                        : `${metrics.nps_aprox}`
                      : "—"
                  }
                  sub={`${metrics.promotores} prom · ${metrics.detractores} det`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Editor de preguntas */}
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-inner">
            <div className="eyebrow">Preguntas extra</div>
            <div style={{ marginTop: 14, display: "grid", gap: 14 }}>
              {draft.map((p, idx) => (
                <PreguntaCard
                  key={idx}
                  pregunta={p}
                  index={idx}
                  total={draft.length}
                  onChange={(patch) => update(idx, patch)}
                  onRemove={() => remove(idx)}
                  onMove={(dir) => move(idx, dir)}
                />
              ))}
              {draft.length === 0 && (
                <p className="helper">
                  Esta alcaldía no ha añadido preguntas extra todavía.
                </p>
              )}
            </div>

            <div className="enc-add-row">
              <span className="enc-add-label">Añadir</span>
              {(["choice", "text", "boolean"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className="btn-outline btn-sm"
                  disabled={draft.length >= cfg.max_preguntas}
                  onClick={() => addPregunta(t)}
                >
                  + {TIPO_LABEL[t]}
                </button>
              ))}
            </div>

            <div style={{ marginTop: 22 }}>
              <button
                type="button"
                className="cta"
                style={{ maxWidth: 280 }}
                disabled={saving}
                onClick={() => void save()}
              >
                <span className="cta-inner">
                  {saving ? "Guardando…" : "Guardar preguntas"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Respuestas recientes */}
        {recent.length > 0 && (
          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-inner">
              <div className="eyebrow">Respuestas recientes</div>
              <ul className="enc-recent-panel">
                {recent.map((r) => (
                  <li key={r.id}>
                    <div className="enc-recent-rating">
                      {"★".repeat(r.rating ?? 0)}
                      <span className="off">{"★".repeat(5 - (r.rating ?? 0))}</span>
                    </div>
                    <div className="enc-recent-body">
                      <div className="enc-recent-rad">
                        <strong>{r.radicado}</strong> · {r.asunto}
                      </div>
                      {r.comentario && (
                        <div className="enc-recent-com">"{r.comentario}"</div>
                      )}
                      {r.respuestas_extra &&
                        Object.keys(r.respuestas_extra).length > 0 && (
                          <ul className="enc-recent-extra">
                            {Object.entries(r.respuestas_extra).map(([pid, rr]) => (
                              <li key={pid}>
                                <span className="lbl">{rr.label}:</span>{" "}
                                {rr.tipo === "boolean"
                                  ? rr.valor
                                    ? "Sí"
                                    : "No"
                                  : String(rr.valor)}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
      </section>
    </Shell>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="enc-panel-metric">
      <div className="enc-panel-metric-label">{label}</div>
      <div className="enc-panel-metric-value">{value}</div>
      {sub && <div className="enc-panel-metric-sub">{sub}</div>}
    </div>
  );
}

function PreguntaCard({
  pregunta,
  index,
  total,
  onChange,
  onRemove,
  onMove,
}: {
  pregunta: EncuestaPregunta;
  index: number;
  total: number;
  onChange: (patch: Partial<EncuestaPregunta>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  return (
    <div className="enc-pregunta-card">
      <div className="enc-pregunta-head">
        <span className="enc-pregunta-num">{String(index + 1).padStart(2, "0")}</span>
        <span className="enc-pregunta-tipo">{TIPO_LABEL[pregunta.tipo]}</span>
        <span className="spacer" />
        <button
          type="button"
          className="btn-outline btn-xs"
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          ↑
        </button>
        <button
          type="button"
          className="btn-outline btn-xs"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
        >
          ↓
        </button>
        <button
          type="button"
          className="btn-outline btn-xs is-danger"
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      <div className="form-row" style={{ marginTop: 12 }}>
        <label className="label">Pregunta</label>
        <input
          type="text"
          className="input"
          value={pregunta.label}
          maxLength={200}
          placeholder="Ej. ¿Volverías a usar nuestro servicio?"
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>
      {pregunta.tipo === "choice" && (
        <div className="form-row">
          <label className="label">Opciones</label>
          {(pregunta.options ?? []).map((opt, i) => (
            <div key={i} className="enc-option-row">
              <input
                type="text"
                className="input"
                value={opt}
                maxLength={100}
                placeholder={`Opción ${i + 1}`}
                onChange={(e) => {
                  const next = [...(pregunta.options ?? [])];
                  next[i] = e.target.value;
                  onChange({ options: next });
                }}
              />
              <button
                type="button"
                className="btn-outline btn-xs is-danger"
                disabled={(pregunta.options?.length ?? 0) <= 2}
                onClick={() =>
                  onChange({
                    options: (pregunta.options ?? []).filter((_, j) => j !== i),
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          {(pregunta.options?.length ?? 0) < 8 && (
            <button
              type="button"
              className="btn-outline btn-sm"
              style={{ marginTop: 8 }}
              onClick={() =>
                onChange({
                  options: [
                    ...(pregunta.options ?? []),
                    `Opción ${(pregunta.options?.length ?? 0) + 1}`,
                  ],
                })
              }
            >
              + Añadir opción
            </button>
          )}
        </div>
      )}
      <label
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginTop: 8,
          cursor: "pointer",
          fontSize: 13,
          color: "var(--ink-2)",
        }}
      >
        <input
          type="checkbox"
          checked={!!pregunta.required}
          onChange={(e) => onChange({ required: e.target.checked })}
        />
        Esta pregunta es obligatoria
      </label>
    </div>
  );
}
